/**
 * Adds a `diff` field to each entry showing change from the previous entry.
 * Entries must be sorted newest-first (descending by date).
 * Positive diff = gained weight, negative = lost weight.
 */
export function computeWeightDiffs(entries) {
  if (!entries || entries.length === 0) return []

  return entries.map((entry, i) => {
    const olderEntry = entries[i + 1]
    const diff = olderEntry ? Number(entry.weight) - Number(olderEntry.weight) : null
    return { ...entry, diff }
  })
}

/**
 * Returns the current consecutive-day logging streak.
 * The streak counts back from today (or yesterday if today has no entry).
 * If the latest entry is older than yesterday, the streak is 0.
 */
export function computeCurrentStreak(entries) {
  if (!entries || entries.length === 0) return 0

  // Get unique dates as YYYY-MM-DD strings, deduped, sorted descending
  const uniqueDates = Array.from(new Set(entries.map((e) => e.date))).sort((a, b) => (a < b ? 1 : -1))

  const today = new Date().toLocaleDateString('en-CA')
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayStr = yesterday.toLocaleDateString('en-CA')

  // If latest entry isn't today or yesterday, the streak is broken
  if (uniqueDates[0] !== today && uniqueDates[0] !== yesterdayStr) {
    return 0
  }

  let streak = 1
  let prevDate = new Date(uniqueDates[0])

  for (let i = 1; i < uniqueDates.length; i++) {
    const currentDate = new Date(uniqueDates[i])
    const expectedPrev = new Date(prevDate)
    expectedPrev.setDate(expectedPrev.getDate() - 1)

    if (currentDate.toLocaleDateString('en-CA') === expectedPrev.toLocaleDateString('en-CA')) {
      streak++
      prevDate = currentDate
    } else {
      break
    }
  }

  return streak
}

/**
 * Returns the longest consecutive-day streak ever recorded.
 */
export function computeLongestStreak(entries) {
  if (!entries || entries.length === 0) return 0

  // Get unique dates, sorted ascending
  const uniqueDates = Array.from(new Set(entries.map((e) => e.date))).sort()

  let longest = 1
  let current = 1

  for (let i = 1; i < uniqueDates.length; i++) {
    const prev = new Date(uniqueDates[i - 1])
    const curr = new Date(uniqueDates[i])
    const expectedNext = new Date(prev)
    expectedNext.setDate(expectedNext.getDate() + 1)

    if (curr.toLocaleDateString('en-CA') === expectedNext.toLocaleDateString('en-CA')) {
      current++
      if (current > longest) longest = current
    } else {
      current = 1
    }
  }

  return longest
}

/**
 * Compute progress stats from weight entries.
 */
export function computeProgressStats(entries, startWeight, goalWeight) {
  if (!entries || entries.length === 0) {
    return {
      currentWeight: null,
      totalLost: 0,
      remaining: goalWeight ? startWeight - goalWeight : 0,
      avgLossRate7Day: 0,
      lowestWeight: null,
      highestWeight: null,
      totalEntries: 0,
      progressPercent: 0,
      currentStreak: 0,
      longestStreak: 0,
    }
  }

  // entries are sorted newest-first
  const currentWeight = Number(entries[0].weight)
  const weights = entries.map((e) => Number(e.weight))
  const lowestWeight = Math.min(...weights)
  const highestWeight = Math.max(...weights)
  const totalLost = startWeight - currentWeight
  const remaining = currentWeight - goalWeight
  const totalRange = startWeight - goalWeight
  const progressPercent = totalRange > 0 ? Math.min(100, Math.max(0, (totalLost / totalRange) * 100)) : 0

  // 7-day average: take entries from the last 7 days
  let avgLossRate7Day = 0
  if (entries.length >= 2) {
    const now = new Date(entries[0].date)
    const sevenDaysAgo = new Date(now)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    const recentEntries = entries.filter((e) => new Date(e.date) >= sevenDaysAgo)
    if (recentEntries.length >= 2) {
      const newest = Number(recentEntries[0].weight)
      const oldest = Number(recentEntries[recentEntries.length - 1].weight)
      const daysDiff = (new Date(recentEntries[0].date) - new Date(recentEntries[recentEntries.length - 1].date)) / (1000 * 60 * 60 * 24)
      if (daysDiff > 0) {
        avgLossRate7Day = ((oldest - newest) / daysDiff) * 7
      }
    }
  }

  return {
    currentWeight,
    totalLost,
    remaining,
    avgLossRate7Day,
    lowestWeight,
    highestWeight,
    totalEntries: entries.length,
    progressPercent,
    currentStreak: computeCurrentStreak(entries),
    longestStreak: computeLongestStreak(entries),
  }
}

/**
 * Adds diffs for each body part compared to the previous measurement.
 * Measurements must be sorted newest-first.
 */
export function computeMeasurementDiffs(measurements) {
  if (!measurements || measurements.length === 0) return []

  const fields = ['neck', 'chest', 'belly', 'waist', 'arm', 'thigh', 'body_fat']

  return measurements.map((m, i) => {
    const older = measurements[i + 1]
    const diffs = {}
    fields.forEach((field) => {
      if (m[field] != null && older && older[field] != null) {
        diffs[field] = Number(m[field]) - Number(older[field])
      } else {
        diffs[field] = null
      }
    })
    return { ...m, diffs }
  })
}

/**
 * Returns an array of milestones with their reached state.
 * Milestones are auto-generated every 5 kg from start down to goal.
 * Each milestone gets a `reachedDate` set to the first date the user
 * crossed below it (looking through entries chronologically).
 */
export function computeMilestones(entries, startWeight, goalWeight) {
  if (!startWeight || !goalWeight || startWeight <= goalWeight) return []

  const milestones = []
  // Round start down to the nearest 5 kg below it
  const firstMilestone = Math.floor(startWeight / 5) * 5
  for (let w = firstMilestone; w >= goalWeight; w -= 5) {
    if (w >= startWeight) continue
    milestones.push({ weight: w, reachedDate: null, isGoal: w === goalWeight })
  }

  // Ensure the goal is included as the final milestone (if not a multiple of 5)
  if (milestones.length === 0 || milestones[milestones.length - 1].weight !== goalWeight) {
    milestones.push({ weight: goalWeight, reachedDate: null, isGoal: true })
  }

  if (!entries || entries.length === 0) return milestones

  // Sort entries chronologically (oldest first)
  const chrono = [...entries].sort((a, b) => (a.date < b.date ? -1 : 1))

  for (const m of milestones) {
    const found = chrono.find((e) => Number(e.weight) <= m.weight)
    if (found) m.reachedDate = found.date
  }

  return milestones
}

/**
 * Returns entries with a `movingAvg` field added.
 * Entries should be in chronological order (oldest first).
 * For each entry, the moving average is computed over the current entry plus
 * the previous (windowSize - 1) entries. If fewer than windowSize entries are
 * available, uses whatever is available (partial window).
 */
export function computeMovingAverage(entries, windowSize) {
  if (!entries || entries.length === 0 || !windowSize) return []

  return entries.map((entry, i) => {
    const start = Math.max(0, i - windowSize + 1)
    const window = entries.slice(start, i + 1)
    const sum = window.reduce((acc, e) => acc + Number(e.weight), 0)
    return { ...entry, movingAvg: sum / window.length }
  })
}

/**
 * Compute aggregate stats from water entries.
 * Entries should be the full list (newest first).
 */
export function computeWaterStats(entries, goalMl) {
  if (!entries || entries.length === 0) {
    return {
      avg7Day: 0,
      avg30Day: 0,
      hitRate: 0,
      bestDay: 0,
      currentStreak: 0,
      totalDays: 0,
    }
  }

  const goal = goalMl || 3000

  // Helpers for filtering by recency
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6)
  const thirtyDaysAgo = new Date(today)
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29)

  const last7 = entries.filter((e) => new Date(e.date) >= sevenDaysAgo)
  const last30 = entries.filter((e) => new Date(e.date) >= thirtyDaysAgo)

  const sum7 = last7.reduce((acc, e) => acc + (e.amount_ml || 0), 0)
  const sum30 = last30.reduce((acc, e) => acc + (e.amount_ml || 0), 0)

  // Average over the calendar window (7 or 30 days), not just days with entries
  const avg7Day = sum7 / 7
  const avg30Day = sum30 / 30

  // Hit rate: % of last 30 days that met the goal (counts only days with entries)
  const daysHit = last30.filter((e) => e.amount_ml >= goal).length
  const hitRate = last30.length > 0 ? (daysHit / last30.length) * 100 : 0

  // Best day ever
  const bestDay = entries.reduce((max, e) => Math.max(max, e.amount_ml || 0), 0)

  // Current streak: consecutive days back from today (or yesterday)
  // with any water logged. Matches the definition the Progress page
  // uses (currentStreakFromSet in analysisUtils.js) — "days you logged
  // water in a row", not "days you hit the goal in a row". Hitting the
  // goal is still tracked via hitRate below.
  const loggedDates = new Set(
    entries.filter((e) => (e.amount_ml || 0) > 0).map((e) => e.date)
  )

  const todayStr = today.toLocaleDateString('en-CA')
  let cursor = new Date(today)
  if (!loggedDates.has(todayStr)) {
    cursor.setDate(cursor.getDate() - 1)
  }
  let currentStreak = 0
  while (true) {
    const cursorStr = cursor.toLocaleDateString('en-CA')
    if (!loggedDates.has(cursorStr)) break
    currentStreak += 1
    cursor.setDate(cursor.getDate() - 1)
  }

  return {
    avg7Day,
    avg30Day,
    hitRate,
    bestDay,
    currentStreak,
    totalDays: entries.length,
  }
}

export function formatWeight(v) {
  if (v == null) return '—'
  return Number(v).toFixed(2)
}

export function formatMeasurement(v) {
  if (v == null) return '—'
  return Number(v).toFixed(1)
}

/**
 * Compute BMI from weight (kg) and height (cm).
 * Returns null if either input is missing or invalid.
 */
export function computeBMI(weightKg, heightCm) {
  if (!weightKg || !heightCm || weightKg <= 0 || heightCm <= 0) return null
  const heightM = heightCm / 100
  return weightKg / (heightM * heightM)
}

/**
 * Returns the BMI category key (matches i18n category keys).
 */
export function getBMICategory(bmi) {
  if (bmi == null) return null
  if (bmi < 18.5) return 'underweight'
  if (bmi < 25) return 'normal'
  if (bmi < 30) return 'overweight'
  return 'obese'
}

/**
 * Compute pace insight: how fast the user needs to lose weight per week to
 * hit their goal weight by their target date, vs. their actual recent rate.
 *
 * @param {object} params
 * @param {number|null} params.currentWeight  current weight in user's storage units (kg)
 * @param {number|null} params.goalWeight     goal weight (kg)
 * @param {string|null} params.goalDate       ISO date string (YYYY-MM-DD)
 * @param {number}      params.recentRatePerWeek  positive number = losing per week
 *
 * Returns null when inputs are insufficient.
 * Otherwise: { paceNeededPerWeek, status: 'on'|'ahead'|'behind', delta, weeksRemaining }
 *   delta is positive when ahead, negative when behind.
 */
/**
 * Returns 00:00 local of Monday in `date`'s ISO week (Mon=start).
 */
export function startOfWeek(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  // JS day: Sun=0, Mon=1, ..., Sat=6. ISO: Mon is the start.
  const day = d.getDay()
  const diff = (day + 6) % 7
  d.setDate(d.getDate() - diff)
  return d
}

/**
 * Returns 00:00 local of the 1st of `date`'s calendar month.
 */
export function startOfMonth(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  d.setDate(1)
  return d
}

function parseDateLocal(str) {
  if (!str) return null
  // YYYY-MM-DD → local midnight. Avoid the UTC parse pitfall of new Date(str).
  return new Date(`${str}T00:00:00`)
}

/**
 * Compute the human-readable summary for the current calendar period.
 * Returns null fields for any section that has no data. `hasData` is true
 * iff at least one section has something worth showing.
 *
 * @param period 'week' | 'month'
 */
export function computeWeeklySummary(params) {
  return computeSummaryInternal({ ...params, period: 'week' })
}
export function computeMonthlySummary(params) {
  return computeSummaryInternal({ ...params, period: 'month' })
}

function computeSummaryInternal({
  weightEntries = [],
  waterEntries = [],
  sessions = [],
  measurements = [],
  mealEntries = [],
  waterGoalMl = 3000,
  now = new Date(),
  period = 'week',
}) {
  const start = period === 'month' ? startOfMonth(now) : startOfWeek(now)
  const end = new Date(now)

  const inPeriod = (dateStr) => {
    const d = parseDateLocal(dateStr)
    return d && d >= start && d <= end
  }

  const daysElapsed = Math.max(
    1,
    Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1
  )

  // --- Weight delta (positive = lost) -------------------------------------
  // Entries arrive newest-first.
  const weightInPeriod = weightEntries.filter((e) => inPeriod(e.date))
  const weightBeforePeriod = weightEntries.find((e) => {
    const d = parseDateLocal(e.date)
    return d && d < start
  })
  let weightDelta = null
  if (weightInPeriod.length >= 2) {
    const newest = Number(weightInPeriod[0].weight)
    const oldest = Number(weightInPeriod[weightInPeriod.length - 1].weight)
    weightDelta = oldest - newest
  } else if (weightInPeriod.length === 1 && weightBeforePeriod) {
    weightDelta = Number(weightBeforePeriod.weight) - Number(weightInPeriod[0].weight)
  }

  // --- Best day (biggest single-day loss within the period) ---------------
  // Walk every in-period entry, diffing against the entry just older.
  let bestDay = null
  if (weightInPeriod.length >= 1) {
    const window = weightBeforePeriod
      ? [...weightInPeriod, weightBeforePeriod]
      : weightInPeriod
    let maxLoss = 0
    for (let i = 0; i < window.length - 1; i++) {
      const newer = window[i]
      const older = window[i + 1]
      if (!inPeriod(newer.date)) continue
      const diff = Number(older.weight) - Number(newer.weight)
      if (diff > maxLoss) {
        maxLoss = diff
        bestDay = { date: newer.date, diff }
      }
    }
    // Sub-100g daily swings are noise; don't celebrate them.
    if (!bestDay || bestDay.diff < 0.1) bestDay = null
  }

  // --- Water --------------------------------------------------------------
  let water = null
  const waterInPeriod = waterEntries.filter((e) => inPeriod(e.date))
  if (waterInPeriod.length > 0) {
    const totalMl = waterInPeriod.reduce((sum, e) => sum + (e.amount_ml || 0), 0)
    const avgPerDay = totalMl / daysElapsed
    const hitGoalCount = waterInPeriod.filter((e) => (e.amount_ml || 0) >= waterGoalMl).length
    water = { avgPerDay, hitGoalCount, daysElapsed }
  }

  // --- Workouts -----------------------------------------------------------
  let workouts = null
  const sessionsInPeriod = sessions.filter((s) => inPeriod(s.date))
  if (sessionsInPeriod.length > 0) {
    const totalVolume = sessionsInPeriod.reduce((sum, s) => sum + (s.totalVolume || 0), 0)
    const totalCardioMin = sessionsInPeriod.reduce((sum, s) => sum + (s.cardioDurationMin || 0), 0)
    workouts = { sessionCount: sessionsInPeriod.length, totalVolume, totalCardioMin }
  }

  // --- Meals --------------------------------------------------------------
  let meals = null
  const mealsInPeriod = mealEntries.filter((m) => inPeriod(m.date))
  if (mealsInPeriod.length > 0) {
    const totalKcal = mealsInPeriod.reduce((sum, m) => sum + (Number(m.kcal) || 0), 0)
    meals = {
      entries: mealsInPeriod.length,
      totalKcal,
      avgKcal: Math.round(totalKcal / daysElapsed),
    }
  }

  // --- Body measurements --------------------------------------------------
  // Compare newest in-period vs the closest reference (pre-period if any,
  // otherwise the oldest in-period). Hide deltas below 0.1 cm.
  let measurementsDelta = null
  const measurementsInPeriod = measurements.filter((m) => inPeriod(m.date))
  if (measurementsInPeriod.length > 0) {
    const pre = measurements.find((m) => {
      const d = parseDateLocal(m.date)
      return d && d < start
    })
    const newest = measurementsInPeriod[0]
    const reference = pre || measurementsInPeriod[measurementsInPeriod.length - 1]
    if (newest && reference && newest !== reference) {
      const fields = ['neck', 'chest', 'belly', 'waist', 'arm', 'thigh']
      const diffs = {}
      let hasAny = false
      for (const f of fields) {
        if (newest[f] != null && reference[f] != null) {
          const d = Number(newest[f]) - Number(reference[f])
          if (Math.abs(d) > 0.1) {
            diffs[f] = d
            hasAny = true
          }
        }
      }
      if (hasAny) measurementsDelta = diffs
    }
  }

  const periodEnd = end.toLocaleDateString('en-CA')
  const periodStart = start.toLocaleDateString('en-CA')
  const hasData =
    weightDelta != null
    || bestDay != null
    || water != null
    || workouts != null
    || measurementsDelta != null
    || meals != null

  return {
    weightDelta,
    bestDay,
    water,
    workouts,
    meals,
    measurements: measurementsDelta,
    daysElapsed,
    hasData,
    period,
    periodStart,
    periodEnd,
  }
}

/**
 * Stats for a single phase scoped to weight entries that fall within
 * its [start_date, end_date] window.
 *
 * @param phase           {start_date, end_date?, start_weight_kg, goal_weight_kg?}
 * @param weightEntries   array sorted newest-first
 * @returns null when there's not enough data, otherwise
 *          { currentWeight, delta, daysInto, progressPercent, recentRatePerWeek }
 */
export function computePhaseStats(phase, weightEntries) {
  if (!phase || !phase.start_date || phase.start_weight_kg == null) return null
  if (!Array.isArray(weightEntries) || weightEntries.length === 0) {
    return {
      currentWeight: null,
      delta: 0,
      daysInto: daysBetween(phase.start_date, new Date()),
      progressPercent: null,
      recentRatePerWeek: 0,
    }
  }

  const today = new Date().toLocaleDateString('en-CA')
  const inPhase = weightEntries.filter((e) => {
    if (e.date < phase.start_date) return false
    if (phase.end_date && e.date > phase.end_date) return false
    if (e.date > today) return false
    return true
  })

  const currentWeight = inPhase.length > 0 ? Number(inPhase[0].weight) : null
  const startWeight = Number(phase.start_weight_kg)
  const goalWeight = phase.goal_weight_kg != null ? Number(phase.goal_weight_kg) : null

  // Positive delta = lost weight (cut/maintain). For bulk, this is negative
  // when going up; the UI labels handle the sign directly.
  const delta = currentWeight != null ? startWeight - currentWeight : 0

  let progressPercent = null
  if (currentWeight != null && goalWeight != null && goalWeight !== startWeight) {
    const targetMove = startWeight - goalWeight
    const actualMove = startWeight - currentWeight
    if (targetMove !== 0) {
      const raw = (actualMove / targetMove) * 100
      progressPercent = Math.min(100, Math.max(0, raw))
    }
  }

  // 7-day rate within the phase. Same shape as computeProgressStats but
  // bounded to phase window.
  let recentRatePerWeek = 0
  if (inPhase.length >= 2) {
    const newestDate = new Date(inPhase[0].date)
    const sevenAgo = new Date(newestDate)
    sevenAgo.setDate(sevenAgo.getDate() - 7)
    const recent = inPhase.filter((e) => new Date(e.date) >= sevenAgo)
    if (recent.length >= 2) {
      const newest = Number(recent[0].weight)
      const oldest = Number(recent[recent.length - 1].weight)
      const daysDiff = (new Date(recent[0].date) - new Date(recent[recent.length - 1].date)) / (1000 * 60 * 60 * 24)
      if (daysDiff > 0) recentRatePerWeek = ((oldest - newest) / daysDiff) * 7
    }
  }

  return {
    currentWeight,
    delta,
    daysInto: Math.max(0, daysBetween(phase.start_date, new Date())),
    progressPercent,
    recentRatePerWeek,
  }
}

// Whole days between two dates (positive when `b` is after `a`).
function daysBetween(a, b) {
  const da = a instanceof Date ? a : new Date(typeof a === 'string' ? `${a}T00:00:00` : a)
  const db = b instanceof Date ? b : new Date(typeof b === 'string' ? `${b}T00:00:00` : b)
  return Math.floor((db.getTime() - da.getTime()) / (1000 * 60 * 60 * 24))
}

export function computePace({ currentWeight, goalWeight, goalDate, recentRatePerWeek }) {
  if (currentWeight == null || goalWeight == null || !goalDate) return null
  const target = new Date(goalDate)
  if (Number.isNaN(target.getTime())) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffMs = target.getTime() - today.getTime()
  const weeksRemaining = diffMs / (1000 * 60 * 60 * 24 * 7)
  if (weeksRemaining <= 0) return null

  const remainingToLose = currentWeight - goalWeight
  if (remainingToLose <= 0) return { paceNeededPerWeek: 0, status: 'ahead', delta: 0, weeksRemaining }

  const paceNeededPerWeek = remainingToLose / weeksRemaining
  const delta = (recentRatePerWeek || 0) - paceNeededPerWeek

  // 0.05 kg/week tolerance for "on pace"
  const tolerance = 0.05
  let status
  if (delta > tolerance) status = 'ahead'
  else if (delta < -tolerance) status = 'behind'
  else status = 'on'

  return { paceNeededPerWeek, status, delta, weeksRemaining }
}
